import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import {
  Button,
  AppHeader,
  Typography,
  Link,
  Stack,
  Icon,
  Card,
} from '@ace-clublabs/shared-component-library';
import { useHistory } from 'react-router-dom';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { UtagType } from '../utils/UtagHelper';
import { getProgressProps, isAutopayRequired } from '../utils/helpers';
import { CartProductCode } from '@ace-clublabs/common-membership-api-types';
import { numberHelper } from '../utils/numberHelper';
import { Pages } from '@lib/types';
import { AppProgressHeader } from '../components/AppProgressHeader';

export const AutopayPage: React.FC = () => {
  const { loadingInfo, getPaymentManifestId, membershipInfo, refreshSession, sendTag, sessionData } =
    useContext(MembershipAppStateContext);
  const history = useHistory();
  const [isTagging, setIsTagging] = useState(true);

  const isAutoPayRequired = membershipInfo ? isAutopayRequired(membershipInfo, sessionData.formData) : false;
  const autopayDiscount = sessionData?.priceCart?.items.find((item) => item.code === CartProductCode.AUTOPAY_DISCOUNT);
  const autopayDiscountAmount = autopayDiscount?.price ?? 0;

  const progressProps = getProgressProps(sessionData.flowType!, Pages.AutopayPage);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }

    sendTag(UtagType.view, 'autopay');
    setIsTagging(false);
  }, [membershipInfo]);

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <div className="autopay-page-view">
      <AppProgressHeader {...progressProps} />

      {loadingInfo.isLoading ? (
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={2} currentStep={loadingInfo.loadingDetails.length} />
      ) : (
        <>
          <Stack className="confirm-autopay-container">
            <Typography variant="h5Medium" className="autopay-title">
              Confirm your Auto Pay enrollment
            </Typography>
            <Typography variant="b1Regular" className="autopay-description">
              Never miss a payment with Auto Pay.
            </Typography>
          </Stack>
          <Card
            sx={{
              maxWidth: '100%',
              backgroundColor: '#FAF8F4',
              text: '#404040',
              padding: '1rem',
              marginTop: '1rem',
              boxShadow: 'none',
            }}
            id="autopay-benefit-card"
          >
            <Stack direction="row" alignItems="center" gap="0.25rem" className="autopay-benefit-card-title">
              <Icon
                iconName="circle-dollar-duotone"
                className="dollar-icon"
                sx={{ color: '#0C7A93', fontSize: '20px' }}
              />
              <Typography variant="b1Medium" color="#0C7A93">
                Auto Pay benefits
              </Typography>
            </Stack>
            <Typography variant="b1Regular" lineHeight="26px">
              <ul style={{ margin: '0.25rem 0 0 -1rem' }} className="autopay-benefit-list">
                {autopayDiscountAmount > 0 && (
                  <li>
                    Save
                    <strong> {`${numberHelper.formatMoney(autopayDiscountAmount)}`} </strong>
                    today by enrolling in Auto Pay
                  </li>
                )}
                <li>Enjoy automatic membership renewal</li>
                <li>Keep your membership active with payments that are always on time</li>
              </ul>
            </Typography>
          </Card>

          <div className="autopay-buttons">
            <Button
              label="Enroll & continue"
              fullWidth
              onClick={() => getPaymentManifestId()}
              variant="contained"
              size="medium"
              id="enrollAutoPayButton"
            ></Button>
            {!isAutoPayRequired ? (
              <Button
                label="Cancel"
                fullWidth
                onClick={() => {
                  history.goBack();
                }}
                variant="outlined"
                size="medium"
                id="cancelAutoPayButton"
              ></Button>
            ) : (
              <></>
            )}
          </div>
          <div className="autopay-terms-section">
            <Typography variant="b3Regular" sx={{ color: '#6C7175' }}>
              By clicking Enroll & continue, I hereby enroll in Auto Pay and have read and agree to the{' '}
              <Link
                className="benefit-link autopay-link"
                id="autopayTermsLink"
                href={`https://app.ace.aaa.com/payments/termsandconditions?variant=mmp&clubcode=${membershipInfo?.clubCode}`}
                target="_blank"
                variant="b3Regular"
              >
                Terms & Conditions
              </Link>
              . Manage your Auto Pay options on My Account.
            </Typography>
          </div>
        </>
      )}
    </div>
  );
};
